import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import API_URL from "../api/baseURL";

const ChecklistViewer = () => {
  const { token, user } = useAuth();
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchChecklists = async () => {
    try {
      const res = await axios.get(`${API_URL}/checklist/workshop/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChecklists(res.data);
    } catch (err) {
      setError('Failed to load checklists');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token && user?._id) fetchChecklists();
    // eslint-disable-next-line
  }, [token, user]);

  const getConditionColor = (condition) => {
    switch (condition) {
      case 'Excellent': case 'Clean': return 'bg-green-100 text-green-700';
      case 'Good': case 'Normal': return 'bg-blue-100 text-blue-700';
      case 'Fair': return 'bg-yellow-100 text-yellow-700';
      case 'Poor': case 'Dirty': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-4">🧾 Submitted Vehicle Checklists</h2>
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
        </div>
      ) : error ? (
        <p className="text-red-500">{error}</p>
      ) : checklists.length === 0 ? (
        <p className="text-gray-500">No checklists submitted yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full bg-white shadow rounded table-auto text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Vehicle</th>
                <th className="px-3 py-2 text-center">Exterior</th>
                <th className="px-3 py-2 text-center">Interior</th>
                <th className="px-3 py-2 text-center">Fuel</th>
                <th className="px-3 py-2 text-center">Odometer</th>
                <th className="px-3 py-2 text-center">Keys</th>
                <th className="px-3 py-2 text-center">Issues</th>
                <th className="px-3 py-2 text-left">Notes</th>
              </tr>
            </thead>
            <tbody>
              {checklists.map((c, idx) => (
                <tr key={c._id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-3 py-2 border-t">
                    {new Date(c.dateReceived || c.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-3 py-2 border-t">
                    <div className="font-medium">{c.vehicleId?.plateNumber || 'N/A'}</div>
                    <div className="text-xs text-gray-500">{c.vehicleId?.brand} {c.vehicleId?.model}</div>
                  </td>
                  <td className="px-3 py-2 border-t text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getConditionColor(c.exteriorCondition)}`}>
                      {c.exteriorCondition || 'N/A'}
                    </span>
                  </td>
                  <td className="px-3 py-2 border-t text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getConditionColor(c.interiorCondition)}`}>
                      {c.interiorCondition || 'N/A'}
                    </span>
                  </td>
                  <td className="px-3 py-2 border-t text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${c.fuelLevel >= 50 ? 'bg-green-100 text-green-700' :
                        c.fuelLevel >= 25 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                      }`}>
                      {c.fuelLevel}%
                    </span>
                  </td>
                  <td className="px-3 py-2 border-t text-center text-gray-600">
                    {c.odometer ? `${c.odometer.toLocaleString()} km` : '-'}
                  </td>
                  <td className="px-3 py-2 border-t text-center">
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                      🔑 {c.numberOfKeys || 1}
                    </span>
                  </td>
                  <td className="px-3 py-2 border-t text-center">
                    <div className="flex flex-wrap gap-1 justify-center">
                      {c.glassBroken && <span className="px-1.5 py-0.5 bg-red-100 text-red-600 text-xs rounded">Glass</span>}
                      {c.sideMirrorDamage && <span className="px-1.5 py-0.5 bg-red-100 text-red-600 text-xs rounded">Mirror</span>}
                      {c.headlightDamage && <span className="px-1.5 py-0.5 bg-red-100 text-red-600 text-xs rounded">Light</span>}
                      {c.tireBoltIssue && <span className="px-1.5 py-0.5 bg-red-100 text-red-600 text-xs rounded">Tire</span>}
                      {!c.glassBroken && !c.sideMirrorDamage && !c.headlightDamage && !c.tireBoltIssue && (
                        <span className="px-1.5 py-0.5 bg-green-100 text-green-600 text-xs rounded">✓ OK</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 border-t text-gray-600 max-w-xs">
                    {c.personalItemsLeft && <div className="text-xs text-orange-600">📦 {c.personalItemsLeft}</div>}
                    {c.technicianComments && <div className="text-xs">{c.technicianComments}</div>}
                    {!c.personalItemsLeft && !c.technicianComments && '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ChecklistViewer;
